import CaCloseIcon from "@/components/icons/CaCloseIcon";
import { Button } from "@/components/ui/Button";
import { useModalAction } from "@/contexts/modal/Context";
import { LoginUserItem, UpsellingProp } from "@/types";
import dayjs from "dayjs";
import {
  MIXPANEL_PAGE_VERSION_DESKTOP,
  MIXPANEL_PAGE_VERSION_MOBILE,
  maixpanelTrackEventProps,
} from "@/mixpanel/MixpanelUtils";
import { useMe } from "@/data/user";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints";
import { useUpsellingTracksMutation } from "@/data/agenda";
import { useNavigate } from "react-router-dom";
import routes from "@/config/routes.ts";


interface Props {
  isDiscounted?: boolean;
  handleClose?: () => void;
}
const Upselling = ({ isDiscounted = true, handleClose }: Props) => {
  const { me } = useMe();
  const breakpoint = useBreakpoint();
  const isSmall = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;
  const navigate = useNavigate();

  const { mutate: upsellingTracks, isLoading } =
    useUpsellingTracksMutation();


  const { closeModal } = useModalAction();

  const mixpanelComfirmData = JSON.stringify(
    maixpanelTrackEventProps(
      "Upselling_Confirm",
      me?.item?.user as LoginUserItem,
    ),
  );

  const mixpanelRejectData = JSON.stringify(
    maixpanelTrackEventProps(
      "Upselling_Reject",
      me?.item?.user as LoginUserItem,
    ),
  );

  const mixpanelCancelData = JSON.stringify(
    maixpanelTrackEventProps(
      "Upselling_Cancel",
      me?.item?.user as LoginUserItem,
    ),
  );

  const handleConfirm = () => {
    try {
      upsellingTracks({
        response: "Yes, I want to keep improving my english",

      });
      closeModal();
      navigate(routes.diploma_request);
      const storedUpsellingStatus = JSON.parse(
        localStorage.getItem("upsellingStatus") as string
      ) as UpsellingProp;
      storedUpsellingStatus.shownDate = dayjs().format("YYYY-MM-DD");
      storedUpsellingStatus.status = "ACCEPTED";
      localStorage.setItem(
        "upsellingStatus",
        JSON.stringify(storedUpsellingStatus)
      );
      handleClose?.();
    } catch (error) {
      console.error(error);
      handleClose?.();
    }
  };

  const handleReject = () => {
    try {
      upsellingTracks({
        response: "No, I don't want to continue",
      });
      closeModal();
      const storedUpsellingStatus = JSON.parse(
        localStorage.getItem("upsellingStatus") as string
      ) as UpsellingProp;
      storedUpsellingStatus.shownDate = dayjs().format("YYYY-MM-DD");
      storedUpsellingStatus.status = "PERMA_CLOSED";
      localStorage.setItem(
        "upsellingStatus",
        JSON.stringify(storedUpsellingStatus)
      );
      handleClose?.();
    } catch (error) {
      console.error(error);
      handleClose?.();
    }
  };


  return (
    <div className="flex flex-col items-center justify-center px-4 rounded-lg bg-brand-light modalShadow md:shadow-none md:px-0 md:bg-transparent">
      <div className="flex justify-end w-full mt-4 md:mt-0 md:px-4">
        <button
          data-track-props={mixpanelCancelData}
          data-track-pageversion={
            isSmall
              ? MIXPANEL_PAGE_VERSION_MOBILE
              : MIXPANEL_PAGE_VERSION_DESKTOP
          }
          onClick={() => {
            closeModal();
            try {
              const storedUpsellingStatus = JSON.parse(
                localStorage.getItem("upsellingStatus") as string,
              ) as UpsellingProp;
              storedUpsellingStatus.shownDate = dayjs().format("YYYY-MM-DD");
              storedUpsellingStatus.status = "TEMP_CLOSED";
              localStorage.setItem(
                "upsellingStatus",
                JSON.stringify(storedUpsellingStatus),
              );
              handleClose?.();
            } catch (error) {
              console.error(error);
              handleClose?.();
            }
          }}
        >
          <CaCloseIcon
            data-track-props={mixpanelCancelData}
            data-track-pageversion={MIXPANEL_PAGE_VERSION_DESKTOP}
          />
        </button>
      </div>
      <div className="flex flex-col gap-4 py-2 md:px-4 md:gap-8">
        <p className="text-base font-bold md:text-2xl text-brand-deep">
          Your course is about to end 😔
        </p>
        {isDiscounted && (
          <p className="text-base md:text-[20px] text-secondary font-medium">
            We are pleased to offer you a{" "}
            <span className="font-bold text-secondary">€50 discount</span> on
            your next course,{" "}
            <span className="font-bold text-secondary">
              valid until Sunday at 20:00
            </span>
            .
          </p>
        )}
        <p className="text-base md:text-[20px] text-secondary font-medium">
          Would you like to continue with a new course?
        </p>
        <div className="flex flex-col gap-1">
          <Button
            variant={"outline"}
            data-track-props={mixpanelComfirmData}
            data-track-pageversion={
              isSmall
                ? MIXPANEL_PAGE_VERSION_MOBILE
                : MIXPANEL_PAGE_VERSION_DESKTOP
            }
            className="text-sm bg-socials-whatsapp md:text-base text-secondary border-secondary"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Yes, I want to keep improving my English"}
          </Button>
          <Button
            className="font-normal text-sm md:text-base underline !py-0 text-secondary"
            variant={"link"}
            data-track-props={mixpanelRejectData}
            data-track-pageversion={
              isSmall
                ? MIXPANEL_PAGE_VERSION_MOBILE
                : MIXPANEL_PAGE_VERSION_DESKTOP
            }
            onClick={handleReject}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "No, I don't want to continue"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Upselling;
